import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import AppShell, { PageHeader } from '../../../components/layout/AppShell';
import { Card, Button, Input, InputLabel, InputGroup } from '../../../components/ui';
import { cn } from '../../../lib/cn';
import { AVATAR_COLOR_OPTIONS, DEFAULT_AVATAR_COLOR, getAvatarColorClasses } from '../../../lib/avatarColors';

function StatTile({ value, label }) {
  return (
    <div className="flex flex-col items-center justify-center p-4">
      <span className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</span>
      <span className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center leading-tight">{label}</span>
    </div>
  );
}

export default function ProfileShow({
  student,
  profile,
  calendar_url,
  has_calendar_token,
  stats = {},
  recent_sessions = [],
  enrolled_courses = [],
  avatar_color_options = [],
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    preferred_name: profile.preferred_name || '',
    email: profile.email || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const initials = profile.initials;

  const handleAvatarColorSelect = (key) => {
    if (savingColor || key === profile.avatar_color) return;
    setSavingColor(true);
    router.patch('/student/profile', { profile: { avatar_color: key } }, {
      preserveScroll: true,
      onFinish: () => setSavingColor(false),
    });
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.patch('/student/profile', { profile: formData }, {
      onFinish: () => {
        setIsSubmitting(false);
        setIsEditing(false);
      },
    });
  };

  const handleCancel = () => {
    setFormData({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      preferred_name: profile.preferred_name || '',
      email: profile.email || '',
    });
    setIsEditing(false);
  };

  const handleGenerateToken = () => {
    router.post('/student/profile/generate_calendar_token');
  };

  const handleRegenerateToken = () => {
    if (confirm('Regenerate your calendar link? This will invalidate any existing subscriptions using the old link.')) {
      router.post('/student/profile/regenerate_calendar_token');
    }
  };

  const handleCopy = async () => {
    if (!calendar_url) return;
    try {
      await navigator.clipboard.writeText(calendar_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.getElementById('calendar-url');
      if (input) {
        input.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const memberSince = new Date(profile.created_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const hasStats = stats.activities_completed > 0 || stats.words_mastered > 0 ||
    stats.streak_days > 0 || stats.sentences_analyzed > 0;

  return (
    <AppShell student={student} scrollTitle={t('profile.title')}>
      <div className="max-w-2xl mx-auto space-y-6">
        <PageHeader
          title={t('profile.title')}
          subtitle={t('profile.subtitle')}
          actions={
            !isEditing && (
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                {t('common.edit')}
              </Button>
            )
          }
        />

        {/* Identity card */}
        <Card className="p-6">
          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InputGroup>
                  <InputLabel htmlFor="first_name">{t('auth.firstName')}</InputLabel>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={handleChange('first_name')}
                    placeholder={t('auth.firstNamePlaceholder')}
                  />
                </InputGroup>

                <InputGroup>
                  <InputLabel htmlFor="last_name">{t('auth.lastName')}</InputLabel>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={handleChange('last_name')}
                    placeholder={t('auth.lastNamePlaceholder')}
                  />
                </InputGroup>
              </div>

              <InputGroup>
                <InputLabel htmlFor="preferred_name">{t('auth.preferredName')}</InputLabel>
                <Input
                  id="preferred_name"
                  value={formData.preferred_name}
                  onChange={handleChange('preferred_name')}
                  placeholder={t('auth.preferredNamePlaceholder')}
                />
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="email">{t('common.email')}</InputLabel>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  placeholder={t('profile.emailPlaceholder')}
                />
              </InputGroup>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? t('common.saving') : t('common.save')}
                </Button>
                <Button type="button" variant="ghost" onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.name')}</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{profile.full_name}</dd>
              </div>
              {profile.preferred_name && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('auth.preferredName')}</dt>
                  <dd className="mt-1 text-gray-900 dark:text-white">{profile.preferred_name}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.email')}</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{profile.email || t('common.notSet')}</dd>
              </div>
              {profile.grade_level && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('profile.gradeLevel')}</dt>
                  <dd className="mt-1 text-gray-900 dark:text-white">{profile.grade_level}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.memberSince')}</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{memberSince}</dd>
              </div>
            </dl>
          )}
        </Card>

        {/* Avatar color */}
        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
            {t('profile.avatarColor')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('profile.avatarColorHint')}
          </p>
          <div className="flex items-center gap-4">
            <div
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0',
                getAvatarColorClasses(profile.avatar_color).gradientClass,
                getAvatarColorClasses(profile.avatar_color).textClass
              )}
            >
              {initials || '?'}
            </div>
            <div className="flex flex-wrap gap-2">
              {(avatar_color_options.length ? avatar_color_options : AVATAR_COLOR_OPTIONS.map((o) => o.key)).map((key) => {
                const isSelected = (profile.avatar_color || DEFAULT_AVATAR_COLOR) === key;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={savingColor}
                    aria-label={key}
                    aria-pressed={isSelected}
                    onClick={() => handleAvatarColorSelect(key)}
                    className={cn(
                      'w-7 h-7 rounded-full transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400 dark:focus-visible:ring-offset-gray-900',
                      getAvatarColorClasses(key).swatchClass,
                      isSelected
                        ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-offset-gray-900 dark:ring-white'
                        : 'hover:scale-110'
                    )}
                  />
                );
              })}
            </div>
          </div>
        </Card>

        {/* Learning stats */}
        <Card className="overflow-hidden">
          <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('profile.learningProgress')}
            </h2>
          </div>
          {hasStats ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-gray-800">
              <StatTile value={stats.activities_completed} label={t('profile.activitiesCompleted')} />
              <StatTile value={stats.words_mastered} label={t('profile.wordsMastered')} />
              <StatTile
                value={stats.streak_days > 0 ? `${stats.streak_days}` : '—'}
                label={t('profile.streakDays')}
              />
              <StatTile value={stats.sentences_analyzed} label={t('profile.sentencesAnalyzed')} />
            </div>
          ) : (
            <div className="px-6 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
              Complete your first activity to start tracking your progress.
            </div>
          )}
          {(stats.words_in_review > 0 || stats.words_onboarding > 0) && (
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex gap-6 text-sm text-gray-500 dark:text-gray-400">
              {stats.words_onboarding > 0 && (
                <span><strong className="text-gray-700 dark:text-gray-300">{stats.words_onboarding}</strong> {t('profile.wordsOnboarding')}</span>
              )}
              {stats.words_in_review > 0 && (
                <span><strong className="text-gray-700 dark:text-gray-300">{stats.words_in_review}</strong> {t('profile.wordsInReview')}</span>
              )}
            </div>
          )}
        </Card>

        {/* Enrolled courses */}
        {enrolled_courses.length > 0 && (
          <Card className="overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('profile.enrolledCourses')}
              </h2>
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {enrolled_courses.map((course) => (
                <li key={course.id} className="px-6 py-3 flex items-center justify-between gap-4">
                  <Link
                    href={`/student/courses/${course.id}`}
                    className="text-sm text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    {course.name}
                  </Link>
                  {course.lessons_total > 0 && (
                    <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                      {course.lessons_completed}/{course.lessons_total} lessons
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Recent sessions */}
        <Card className="overflow-hidden">
          <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('profile.recentSessions')}
            </h2>
          </div>
          {recent_sessions.length === 0 ? (
            <p className="px-6 py-4 text-sm text-gray-400 dark:text-gray-500">
              {t('profile.noRecentSessions')}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {recent_sessions.map((session) => (
                <li key={session.id} className="px-6 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-900 dark:text-white">{session.activity_name}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                    {new Date(session.completed_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Calendar Subscription */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('profile.calendarSubscription')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('profile.calendarSyncDescription')}
          </p>

          {has_calendar_token && calendar_url ? (
            <div className="space-y-4">
              <InputGroup>
                <InputLabel htmlFor="calendar-url">{t('profile.feedUrl')}</InputLabel>
                <div className="flex gap-2">
                  <Input
                    id="calendar-url"
                    value={calendar_url}
                    readOnly
                    onClick={(e) => e.target.select()}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCopy}
                    className="flex-shrink-0"
                  >
                    {copied ? t('common.copied') : t('common.copy')}
                  </Button>
                </div>
              </InputGroup>

              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateToken}
                >
                  {t('profile.regenerateLink')}
                </Button>
              </div>

              <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('profile.privateTokenWarning')}
                </p>
              </div>
            </div>
          ) : (
            <Button type="button" variant="primary" onClick={handleGenerateToken}>
              {t('profile.generateCalendarLink')}
            </Button>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
